/**
 * =============================================================================
 * WlBillingAgingCard
 *
 * Card de aging de inadimplência (padrão Stripe "Faturas pendentes"):
 *   - Total geral (soma de a vencer + todas faixas de vencido)
 *   - Barra segmentada colorida (proporção por faixa)
 *   - Tabela: Idade da fatura | Faturas (count) | Valor
 *
 * v1.1.0 — Cada faixa com valor > 0 passa a ser clicável e abre o modal
 * `WlBillingAgingDetailModal` com a lista paginada das parcelas do bucket.
 *
 * Ícone (?) no header explica que considera TODAS as parcelas em aberto do
 * tenant master (independente do período do filtro superior).
 *
 * @version 1.1.0
 * RELEASE:
 *   2026-07-22 v1.0.0 - Versão inicial.
 *   2026-07-22 v1.1.0 - Linhas clicáveis abrem modal de drill-down.
 * =============================================================================
 */
import { useState } from 'react';
import { FinancialPendingInvoicesCard } from '@/components/financial/shared/FinancialPendingInvoicesCard';
import type {
  WlBillingAgingBucket,
  WlBillingAgingRow,
  WlBillingKpisPayload,
} from '@/hooks/wl/useWlBillingKpis';
import { WlBillingAgingDetailModal } from './WlBillingAgingDetailModal';

interface Props {
  data?: WlBillingKpisPayload;
  isLoading?: boolean;
}

export function WlBillingAgingCard({ data, isLoading }: Props) {
  // Estado do drill-down: bucket + label do bucket selecionado
  const [detailBucket, setDetailBucket] = useState<WlBillingAgingBucket | null>(null);
  const [detailLabel, setDetailLabel] = useState<string | null>(null);

  const rows = data?.aging ?? [];

  /** Abre modal de detalhes para o bucket clicado (somente se tiver valor). */
  const handleRowClick = (r: WlBillingAgingRow) => {
    if (Number(r.total || 0) <= 0 && Number(r.count || 0) <= 0) return;
    setDetailBucket(r.bucket);
    setDetailLabel(r.label);
  };

  return (
    <>
      <FinancialPendingInvoicesCard
        rows={rows}
        isLoading={isLoading || !data}
        helpText="Aging de todas as parcelas em aberto do tenant master. A faixa a vencer considera o mês atual; as demais faixas usam a diferença entre o vencimento e hoje. Clique em uma faixa para ver as parcelas."
        onRowClick={handleRowClick}
      />

      {/* Modal de drill-down */}
      <WlBillingAgingDetailModal
        open={!!detailBucket}
        onClose={() => setDetailBucket(null)}
        bucket={detailBucket}
        label={detailLabel}
      />
    </>
  );
}
